<template>
  <ezine-page-editor v-if="editPage" class="flex-grow" :page-id="editPage" @name-changed="updatePageName($event)" :logo="form.logo">
    <button slot="actions" class="bg-white hover:bg-grey-light rounded border border-primary px-3 py-2 mr-3" @click="editPage = null">
      <i class="fas fa-arrow-left"></i> Exit Page Viewer / Editor
    </button>
  </ezine-page-editor>

  <div v-else>
    <div class="flex flex-wrap -mx-2">
      <div class="w-full md:w-1/4 px-2">
        <div>
          <h3 class="text-lg font-bold mb-6">Your Pages</h3>

          <p class="text-xs mb-6" v-if="editable"><i class="fas fa-arrows-alt"></i> Drag and drop to add pages to the layout.</p>

          <div class="sticky pin-t">
            <details v-for="(pageGroup, i) in visiblePageGroups" :key="pageGroup.id" class="mb-6" :open="i === 0">
              <summary class="cursor-pointer mb-3">
                <i class="fas fa-folder fa-lg" :class="{'text-blue': pageGroup.assignable_type === 'App\\Models\\Network', 'text-pink': pageGroup.assignable_type === 'App\\Models\\EzineCampaign'}"></i> {{ pageGroup.name }}
              </summary>
              <div class="flex flex-col">
                <div v-if="[3, 4].includes(pageGroup.type_id) && type === 'campaign'" class="rounded bg-blue-lightest border px-3 py-2 text-sm cursor-move mb-1 italic font-bold text-blue-dark" draggable @dragstart="pageGroupStartAdd($event, pageGroup)">
                  Latest Page
                </div>
                <div v-if="pageGroup.pages.length === 0" class="italic mt-3 block text-grey-dark text-sm">
                  This group is empty.
                </div>
                <div v-else>
                  <div v-for="page in pageGroup.pages" :key="page.id" class="flex rounded bg-grey-lighter border text-sm mb-1 overflow-hidden" :class="{'cursor-move': pageGroup.type_id !== 0 && editable}" :draggable="pageGroup.type_id !== 0 && editable" @dragstart="pageStartAdd($event, page)" v-if="pageGroup.type_id != 1">
                    <div class="flex-grow px-3 py-2">
                      <strong>{{ page.name }}</strong>
                      <span class="block text-xs text-grey-dark">Updated: {{ page.updated_at | date }}</span>
                    </div>
                    <div class="flex flex-col border-l">
                      <button v-if="pageGroup.assignable_type === 'App\\Models\\Network'" class="flex-1 px-3 hover:bg-grey" title="Preview Page" @click="openEditor(page.id)">
                        <i class="fas fa-eye"></i>
                      </button>
                      <button v-if="pageGroup.assignable_type !== 'App\\Models\\Network' && editable" class="flex-1 px-3 hover:bg-grey" title="Edit Page" @click="openEditor(page.id)">
                        <i class="fas fa-pencil-alt"></i>
                      </button>
                      <button v-if="pageGroup.assignable_type === 'App\\Models\\EzineCampaign' && editable && pageGroup.type_id !== 0" class="flex-1 px-3 border-t hover:bg-red-dark hover:text-white" title="Delete Page" @click="deletePageId = page.id">
                        <i class="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <button v-if="pageGroup.type_id === 2 && editable" class="block rounded bg-green-lightest hover:border-green-dark hover:bg-green-dark hover:text-white border px-3 py-2 text-sm mb-1" @click="newPageForm.ezine_page_group_id = pageGroup.id">
                  <i class="fas fa-plus-circle"></i> Create New Page
                </button>
                <button v-if="(pageGroup.type_id === 1) && editable && type === 'campaign'" class="block rounded bg-green-lightest hover:border-green-dark hover:bg-green-dark hover:text-white border px-3 py-2 text-sm mb-1" @click="openEzinePageModuleModal('create')">
                  <i class="fas fa-plus-circle"></i> Create New Page
                </button>
              </div>
            </details>
          </div>
        </div>
      </div>

      <div class="w-full md:w-1/2 px-2">
        <div>
          <h3 class="text-lg font-bold mb-6">Page Layout</h3>

          <p class="text-xs mb-3" v-if="editable"><i class="fas fa-arrows-alt-v"></i> Drag and drop to re-order.</p>

          <transition-group name="pages" tag="div" class="relative">
            <div v-for="(page, i) in pageLayout" :key="page.ref" class="w-full flex items-center" :draggable="page.type_id !== 0 && editable" @dragstart="pageStartMove($event, page.ref)" @drop="pageEndMove($event, page.ref)" @dragenter.prevent @dragover.prevent>
              <div class="w-10 text-right font-bold mr-3 text-sm mt-3">
                {{ i+1 }}.
              </div>
              <div class="flex-grow border rounded text-sm flex items-center overflow-hidden mt-3" :class="{'bg-pink-lightest': [0, 6].includes(page.type_id), 'bg-green-lightest': page.type_id === 1, 'bg-blue-lightest': page.type_id === 3, 'bg-yellow-lightest': page.type_id === 4, 'bg-grey-lighter': ![0, 1, 3, 4, 6].includes(page.type_id), 'cursor-ns-resize': page.type_id !== 0 && editable}">
                <div class="flex-grow px-3 py-2" :class="{italic: page.is_group}">
                  {{ page.name }}
                  <ul v-if="page.locations" class="text-xs italic pl-6">
                    <li v-for="(location, i) in page.locations" :key="i">
                      {{ location }}
                    </li>
                  </ul>
                </div>
                <i class="fas fa-fw mx-3" :class="{'fa-bookmark text-pink-dark': [0, 6].includes(page.type_id), 'fa-home text-green-dark': page.type_id === 1, 'fa-pen-alt text-blue-dark': page.type_id === 3, 'fa-star text-yellow-dark': page.type_id === 4, 'fa-file text-grey-dark': ![0, 1, 3, 4, 6].includes(page.type_id)}"></i>
                <div v-if="page.type_id === 0 || !editable" class="px-3 self-stretch flex items-center border-l" title="This page cannot be moved or removed">
                  <i class="fas fa-fw fa-lock"></i>
                </div>
                <button v-else class="px-3 border-l hover:bg-red-dark hover:text-white self-stretch" title="Remove page from the campaign" @click="removePage(page.ref)">
                  <i class="fas fa-fw fa-trash-alt"></i>
                </button>
                <button v-if="type === 'campaign' && page.type_id === 1 && page.data" class="px-3 border-l hover:bg-red-600 hover:text-white self-stretch" title="Edit Page" @click="openEzinePageModuleModal('edit', page)">
                  <i class="fas fa-pencil-alt"></i>
                </button>
              </div>
            </div>
          </transition-group>
        </div>
      </div>

      <div class="w-full md:w-1/4 px-2">
        <div>
          <h3 class="text-lg font-bold mb-6">Key</h3>
          <div class="flex items-center mb-3">
            <div class="w-10 h-10 bg-pink-lightest text-pink-dark mr-3 flex items-center justify-center border rounded">
              <i class="fas fa-fw fa-bookmark"></i>
            </div>
            Cover
          </div>
          <div class="flex items-center mb-3">
            <div class="w-10 h-10 bg-grey-lighter text-grey-dark mr-3 flex items-center justify-center border rounded">
              <i class="fas fa-fw fa-file"></i>
            </div>
            Custom Page
          </div>
          <div class="flex items-center mb-3">
            <div class="w-10 h-10 bg-green-lightest text-green-dark mr-3 flex items-center justify-center border rounded">
              <i class="fas fa-fw fa-home"></i>
            </div>
            Property Page
          </div>
          <div class="flex items-center mb-3">
            <div class="w-10 h-10 bg-blue-lightest text-blue-dark mr-3 flex items-center justify-center border rounded">
              <i class="fas fa-fw fa-pen-alt"></i>
            </div>
            Article
          </div>
          <div class="flex items-center mb-3">
            <div class="w-10 h-10 bg-yellow-lightest text-yellow-dark mr-3 flex items-center justify-center border rounded">
              <i class="fas fa-fw fa-star"></i>
            </div>
            Promotion
          </div>
        </div>
      </div>
    </div>

    <modal v-if="newPageForm.ezine_page_group_id" @leave="newPageForm.ezine_page_group_id = null" show-close-btn width="240px">
      <h2 class="font-bold text-sm mb-1">New Page</h2>
      <p class="mb-6">To create a new page, enter the page name below and click the 'Create Page' button.</p>

      <div class="mb-6">
        <label>Name:</label>
        <input v-model="newPageForm.name"
          class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': newPageErrorBag.get('name') }" />
        <small class="block text-red-dark text-medium mt-2" v-if="newPageErrorBag.get('name')" v-text="newPageErrorBag.get('name')"></small>
      </div>

      <div class="flex justify-end mt-6">
        <button class="bg-transparent text-white py-2 px-6 text-sm bg-green-dark rounded shadow" :class="{'hover:bg-green': !creating}" @click="createPage" :disabled="creating">
          <loader v-if="creating" fill-color="white" />
          <span v-else><i class="fas fa-plus-circle"></i> Create Page</span>
        </button>
      </div>
    </modal>
    <modal ref="ezinePageModuleModal" v-if="showEzinePageModuleModal && type === 'campaign'" @leave="showEzinePageModuleModal = false" show-close-btn width="600px">
      <ezine-property-page-editor :ezinePageModule="ezinePageModule" :pageGroups="pageGroups" :offices="offices" :form="form" :studentLettingsList="studentLettingsList" @close="closeEzinePageModuleModal">
      </ezine-property-page-editor>
    </modal>
    <deletion-modal
      v-if="deletePageId"
      @confirmed="deletePage(deletePageId)"
      @leave="deletePageId = null"
      @dismissed="deletePageId = null">
      <p>Are you sure you want to permanently delete this page?</p>
    </deletion-modal>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import { map, find, filter } from 'lodash'
import randomstring from 'randomstring'
import DeletionModal from '@/components/Modals/DeletionModal'
import EzinePageEditor from '@/components/Ezines/EzinePageEditor.vue'
import ErrorBag from '@/ErrorBag'
import EzinePropertyPageEditor from '@/product-modules/ezines/EzinePropertyPageEditor.vue'
export default {
  components: { EzinePageEditor, DeletionModal, EzinePropertyPageEditor },
  props: {
    errorBag: Object,
    value: Object,
    pageGroups: Array,
    type: String,
    editable: {
      type: Boolean,
      default: true
    },
    offices: Array
  },
  data () {
    return {
      form: this.value,
      movingPage: null,
      editPage: null,
      deletePageId: null,
      newPageForm: {
        ezine_page_group_id: null,
        name: null
      },
      creating: false,
      newPageErrorBag: new ErrorBag(),
      ezinePageModule: {
        ezine_page_group_id: null,
        data: {
          name: '',
          rental: false,
          student_lettings: null,
          tags: null,
          property_type_group_ids: []
        },
        office_id: this.offices.length > 0 ? this.offices[0].id : '',
        locations: []
      },
      studentLettingsList: [
        { id: null, name: 'No filter' },
        { id: 1, name: 'Student Lettings Only' },
        { id: 0, name: 'No Student Lettings' }
      ],
      showEzinePageModuleModal: false
    }
  },
  computed: {
    pageLayout () {
      if (!this.form || !this.pageGroups) {
        return []
      }
      return map(this.form.pages, (page) => {
        const isGroup = this.type === 'campaign' && page.content_type === 'App\\Models\\EzinePageGroup'
        const pageGroup = find(this.pageGroups, (group) => group.id === (isGroup ? page.content_id : (this.type === 'campaign' ? page.content.ezine_page_group_id : page.ezine_page_group_id)))
        const locations = isGroup ? [] : filter(this.type === 'campaign' ? page.content.modules : page.modules, (m) => m.module.component === 'properties-module' && m.data.location).map((m) => m.data.location)
        const name = this.type === 'campaign' ? page.content.name : page.name
        const data = isGroup ? [] : filter(this.type === 'campaign' ? page.content.modules : page.modules, (m) => m.module.component === 'properties-module' && m.data).map((m) => m.data)
        const officeId = isGroup ? [] : filter(this.type === 'campaign' ? page.content.modules : page.modules, (m) => m.module.component === 'properties-module' && m.office_id).map((m) => m.office_id)
        return {
          ref: page.ref,
          name: isGroup ? `${name} - Next Latest Page` : name,
          locations,
          type_id: pageGroup.type_id,
          is_group: isGroup,
          data: data && data.length > 0 ? data[0] : {},
          office_id: officeId.length > 0 ? officeId[0] : ''
        }
      })
    },
    visiblePageGroups () {
      let campaignPageGroups = filter(this.pageGroups, (group) => (![1].includes(group.type_id) || this.type === 'campaign') && group.assignable_type === 'App\\Models\\EzineCampaign')
      let otherPageGroups = filter(this.pageGroups, (group) => ![1].includes(group.type_id) && group.assignable_type !== 'App\\Models\\EzineCampaign')

      return campaignPageGroups.concat(otherPageGroups)
    }
  },
  methods: {
    removePage (ref) {
      const page = find(this.form.pages, (page) => page.ref === ref)
      this.form.pages.splice(this.form.pages.indexOf(page), 1)

      // Unassign any offices from the campaign that no longer have pages
      let includedOffices = this.form.pages.map((p) => p.content.modules)
        .flat(1)
        .filter((m) => m.module.component === 'properties-module' && m.office_id !== null)
        .map((m) => m.office_id)
      includedOffices = includedOffices.filter((id, i) => includedOffices.indexOf(id) === i)
      this.form.offices_list = includedOffices
    },
    pageGroupStartAdd (event, pageGroup) {
      event.dataTransfer.setData('text/plain', 'add_group')
      this.movingPage = pageGroup
    },
    pageStartAdd (event, page) {
      event.dataTransfer.setData('text/plain', 'add_page')
      this.movingPage = page
    },
    pageStartMove (event, ref) {
      event.dataTransfer.setData('text/plain', 'move')
      this.movingPage = find(this.form.pages, (page) => page.ref === ref)
    },
    pageEndMove (event, secondPageRef) {
      if (!this.movingPage) {
        return
      }
      const secondPage = find(this.form.pages, (page) => page.ref === secondPageRef)
      const newIndex = Math.max(this.form.pages.indexOf(secondPage), 1)
      const mode = event.dataTransfer.getData('text/plain')
      if (mode === 'add_group') {
        this.form.pages.splice(newIndex, 0, {
          ref: randomstring.generate(),
          content_type: 'App\\Models\\EzinePageGroup',
          content_id: this.movingPage.id,
          content: this.movingPage
        })
        this.movingPage = null
        return
      }
      if (mode === 'add_page') {
        if (this.type === 'campaign') {
          this.form.pages.splice(newIndex, 0, {
            ref: randomstring.generate(),
            content_type: 'App\\Models\\EzinePage',
            content_id: this.movingPage.id,
            content: this.movingPage
          })
        } else {
          this.form.pages.splice(newIndex, 0, {
            ref: randomstring.generate(),
            ...this.movingPage
          })
        }
        this.movingPage = null
        return
      }
      const oldIndex = this.form.pages.indexOf(this.movingPage)
      this.form.pages.splice(oldIndex, 1)
      this.form.pages.splice(newIndex, 0, this.movingPage)
      this.movingPage = null
    },
    openEditor (pageId) {
      this.editPage = pageId
    },
    updatePageName (name) {
      for (let i = 0; i < this.pageGroups.length; i++) {
        for (let j = 0; j < this.pageGroups[i].pages.length; j++) {
          if (this.pageGroups[i].pages[j].id === this.editPage) {
            this.pageGroups[i].pages[j].name = name
            return
          }
        }
      }
    },
    deletePage (pageId) {
      v2Api.delete(`/ezine-pages/${pageId}`).then(() => {
        for (let i = 0; i < this.pageGroups.length; i++) {
          for (let j = 0; j < this.pageGroups[i].pages.length; j++) {
            if (this.pageGroups[i].pages[j].id === pageId) {
              this.pageGroups[i].pages.splice(j, 1)
            }
          }
        }

        for (let i = 0; i < this.form.pages.length; i++) {
          if (this.form.pages[i].content_type === 'App\\Models\\EzinePage' && this.form.pages[i].content_id === pageId) {
            this.form.pages.splice(i, 1)
            i--
          }
        }

        this.deletePageId = null
      })
    },
    createPage () {
      this.creating = true
      this.newPageErrorBag.clear()

      v2Api.post('ezine-pages', this.newPageForm).then((response) => {
        let group = find(this.pageGroups, (pageGroup) => pageGroup.id === response.data.ezine_page_group_id)
        if (!group) {
          return
        }

        group.pages.unshift(response.data)
        this.editPage = response.data.id
        this.creating = false
        this.newPageForm.ezine_page_group_id = null
      }, (errors) => {
        this.newPageErrorBag.store(errors.response.data)
        this.creating = false
      })
    },
    openEzinePageModuleModal (action, existingPageModule = null) {
      if (action === 'edit' && existingPageModule) {
        this.ezinePageModule = existingPageModule
      } else {
        this.resetEzinePageModule()
      }
      this.showEzinePageModuleModal = true
    },
    closeEzinePageModuleModal () {
      this.showEzinePageModuleModal = false
      this.resetEzinePageModule()
    },
    resetEzinePageModule () {
      this.ezinePageModule = {
        data: {
          name: '',
          rental: false,
          student_lettings: null,
          tags: null,
          property_type_group_ids: this.form ? this.form.property_type_group_ids : []
        },
        office_id: this.offices.length > 0 ? this.offices[0].id : '',
        locations: []
      }
    }
  },
  watch: {
    form: {
      handler () {
        this.$emit('input', this.form)
      },
      deep: true
    }
  }
}
</script>

<style>
.pages-move,
.pages-enter-active,
.pages-leave-active {
transition: all 0.5s ease;
}
.pages-enter-from,
.pages-leave-to {
opacity: 0;
transform: scale(.5);
}
.pages-leave-active {
position: absolute;
}
</style>
